import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { contactAPI } from '../lib/api';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function ContactPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactAPI.send(form);
      setSent(true);
      toast.success(t('contact.success'));
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch { toast.error(t('common.error')); }
    setLoading(false);
  };

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="contact-page">
      <div className="mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('contact.title')}</h1>
        <p className="text-zinc-500 mt-2">We'd love to hear from you. Reach out anytime.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Contact Info */}
        <div className="space-y-4">
          {[
            { icon: Mail, label: 'Email', value: 'info@xdomainx.com' },
            { icon: Phone, label: 'Phone', value: '+1 (555) 000-0000' },
            { icon: MapPin, label: 'Location', value: 'Global / Remote' },
          ].map((item, i) => (
            <div key={i} className="p-5 bg-[#0A0A0A] border border-white/5 rounded-sm flex items-start gap-4">
              <item.icon className="h-5 w-5 text-[#00E5FF] mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] text-zinc-600 uppercase tracking-wider">{item.label}</div>
                <div className="text-sm text-white mt-0.5">{item.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="lg:col-span-2">
          {sent ? (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-12 text-center">
              <CheckCircle className="h-12 w-12 text-[#00FF94] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">{t('contact.success')}</h3>
              <p className="text-zinc-500">We'll get back to you shortly.</p>
              <Button onClick={() => setSent(false)} variant="outline" className="mt-6 border-zinc-800 text-zinc-400 rounded-sm">Send another message</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('contact.name')}</Label>
                  <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required name="name" placeholder="Your full name"
                    className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="contact-name-input" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('contact.email')}</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} required
                    className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="contact-email-input" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('contact.subject')}</Label>
                <Input value={form.subject} onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))} required
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="contact-subject-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('contact.message')}</Label>
                <Textarea value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} required rows={5}
                  className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none" data-testid="contact-message-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="h-11 px-8 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="contact-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> {t('contact.send')}</>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
